import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogIn, Radio } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-white uppercase">AYO<span className="text-gray-500 ml-1 font-medium text-lg normal-case">source</span></h1>
        </div>

        <h2 className="font-heading text-2xl font-bold text-white mb-2">Sign In</h2>
        <p className="text-gray-500 text-sm mb-8">Access your streaming ad control panel</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Email</Label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@stream.com"
              className="bg-black/40 border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-gray-600 h-12"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Password</Label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="bg-black/40 border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-gray-600 h-12"
            />
          </div>
          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold uppercase tracking-widest rounded-none transition-all duration-200"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="flex items-center gap-2"><LogIn className="w-4 h-4" /> Sign In</span>
            )}
          </Button>
        </form>

        <p className="text-center text-gray-500 text-sm mt-8">
          Don&apos;t have an account?{' '}
          <Link to="/register" data-testid="go-to-register-link" className="text-[#FF3B30] hover:underline font-medium">Create Account</Link>
        </p>
      </div>
    </div>
  );
}
